import { FullStory, isInitialized as isFullStoryInitialized } from '@fullstory/browser';
import type { PrometrixBuildingInfo } from 'api/prometrix';
import type { SmartyAutoCompleteResponse } from 'api/smarty/smarty.types';
import type { TransformedSubmission } from 'api/submission';
import {
  approvePropertyUWReportType,
  BDG_OCCUPANCY_CODE,
  BINARY_CONSTANTS,
  defaultCurrency,
  defaultDateformat,
  DRIVER_VEHICLE_VALIDATION_LOWER_LIMIT_VERSION,
  employerExposurePerilNames,
  entityTypes,
  EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES,
  EXPANDED_HOTEL_PAYROLL_WORKSHEET_INDUSTRIES,
  FULLSTORY_EVENTS,
  HAS_SCHEDULED_BUILDINGS_CODE,
  SCHEDULED_BUILDING_OCCUPANCY_VALUES,
  STATE_SET,
  STATE_ZIPCODE_REGEX,
  STATES,
  STATES_FULLNAME,
  WC_STATE_EXCLUSION_MESSAGES,
  wcExcludedIndustryNames,
} from 'common/constants';
import type { ShowCondition } from 'components/lib/QuestionEngine';
import { every, includes, isArray, isEmpty } from 'lodash-es';
import { DateTime } from 'luxon';
import type {
  WCExclusionParams,
  WCExclusionResult,
} from 'routes/Eligibility/steps/EligibilityQuestions/EligibilityQuestions.types';
import type { IProperty } from 'routes/Exposures/Properties/Properties.types';
import type { GtmEvent } from 'types/gtm';

/**
 * Checks field has value or not.
 *
 * By default it checks, `undifined`, `''`, and `null`
 *
 * With second paramether can add additionalCheks.
 * E.g: `['-']`, will check `-` as well.
 *
 * @param {unknown} value - The value to check.
 * @param {unknown[]} [additionalCheck] - Additional values to check against.
 * @returns {boolean} True if the value is present, false otherwise.
 */
export const isValuePresent = (value: unknown, additionalCheck?: unknown[]): boolean => {
  const conditions = [undefined, '', null, ...(additionalCheck ?? [])];

  return !includes(conditions, value);
};

/**
 * Returns true if any of the given field codes has a value in the source object.
 */
export const hasWorksheetDataIn = (
  source: Record<string, unknown> | null | undefined,
  fieldCodes: readonly string[],
): boolean => fieldCodes.some((code) => isValuePresent(source?.[code]));

/**
 * Returns true if any of the given fields has a code that matches the provided codes.
 */
export const fieldsContainAnyCode = (
  fields: Array<{ code?: string }> | null | undefined,
  codes: readonly string[],
): boolean => (fields ?? []).some((f) => f?.code && codes.includes(f.code));

/**
 * https://dev.azure.com/radity-gmbh/THREE-insurance/_workitems/edit/8984/
 */
export const formatLocation = (addressInfo: SmartyAutoCompleteResponse) => {
  const { street_line = '', secondary = '', city = '', state = '', zipcode = '' } = addressInfo;

  const parts = [street_line, secondary, city, state, zipcode];

  return {
    storing: street_line ? parts.map((part) => JSON.stringify(part ?? '')).join(',') : '-',
    showing: {
      tail: ` ${state} ${zipcode}`,
      head: street_line ? `${street_line},${secondary ? ` ${secondary}, ` : ' '}${city}` : '-',
    },
  };
};

/**
 * Parse location that stored as location formula
 *
 * https://dev.azure.com/radity-gmbh/THREE-insurance/_workitems/edit/8984/
 */
export const parseLocation = (location?: string) => {
  const empty = {
    street_line: '',
    secondary: '',
    city: '',
    state: '',
    zipcode: '',
  };

  if (!location) return empty;

  try {
    let parsed = location.split('","');
    if (parsed.length === 5) {
      const cleaned = location.replace(/^"|"$/g, '');
      // Split by reverse order to parse correctly in case address part ends with ", (double quote + comma)
      parsed = cleaned
        .split('')
        .reverse()
        .join('')
        .split('","')
        .map((x) => x.split('').reverse().join(''))
        .reverse();
      parsed = parsed.map((part) => JSON.parse(`"${part}"`));
    } else {
      parsed = location.split(',') ?? [];
    }
    const [street_line = '', secondary = '', city = '', state = '', zipcode = ''] = parsed;

    return {
      street_line,
      secondary,
      city,
      state,
      zipcode,
    };
  } catch (_e) {
    const parts = location.split(',');

    const [street_line = '', secondary = '', city = '', state = '', zipcode = ''] = parts;

    return {
      street_line,
      secondary,
      city,
      state,
      zipcode,
    };
  }
};

/**
 * Helper function to create address result object
 */
export const createAddressResult = (addressParts: string[], state: string, zipcode: string) => {
  if (addressParts.length === 1) {
    return {
      street_line: addressParts[0],
      secondary: '',
      city: '',
      state,
      zipcode,
    };
  } else if (addressParts.length === 2) {
    return {
      street_line: addressParts[0],
      secondary: '',
      city: addressParts[1],
      state,
      zipcode,
    };
  } else if (addressParts.length >= 3) {
    return {
      street_line: addressParts[0],
      secondary: addressParts.slice(1, -1).join(', '),
      city: addressParts[addressParts.length - 1],
      state,
      zipcode,
    };
  }

  return {
    street_line: '',
    secondary: '',
    city: '',
    state,
    zipcode,
  };
};

/**
 * Helper function to validate if a string is a valid US state code
 */
export const isValidStateCode = (stateCode: string): boolean =>
  /^[A-Z]{2}$/i.test(stateCode) && STATE_SET.has(stateCode.toUpperCase());

/**
 * Helper function to parse state and zipcode from the last parts
 */
export const parseStateAndZip = (
  parts: string[],
): { state: string; zipcode: string; addressParts: string[] } => {
  // Special case: if only one part and it doesn't look like "state zipcode", treat as address
  if (parts.length === 1) {
    const singlePart = parts[0];
    const stateZipMatch = singlePart.match(STATE_ZIPCODE_REGEX);
    if (stateZipMatch) {
      // Looks like "state zipcode" format
      return {
        state: stateZipMatch[1].trim(),
        zipcode: stateZipMatch[2].trim(),
        addressParts: [],
      };
    } else {
      // Treat as address part, not state
      return {
        state: '',
        zipcode: '',
        addressParts: parts,
      };
    }
  }

  const lastPart = parts[parts.length - 1];

  // Check if last part is exactly 5 digits (separate zipcode)
  if (/^\d{5}$/.test(lastPart) && parts.length >= 2) {
    const secondToLastPart = parts[parts.length - 2];

    if (isValidStateCode(secondToLastPart)) {
      return {
        state: secondToLastPart.toUpperCase(),
        zipcode: lastPart,
        addressParts: parts.slice(0, -2),
      };
    } else {
      // Combine invalid state with zipcode
      const combinedLastTwo = `${secondToLastPart} ${lastPart}`;
      const stateZipMatch = combinedLastTwo.match(STATE_ZIPCODE_REGEX);
      if (stateZipMatch) {
        return {
          state: stateZipMatch[1].trim(),
          zipcode: stateZipMatch[2].trim(),
          addressParts: parts.slice(0, -2),
        };
      }
    }
  }

  // Try to parse "state zipcode" combined format
  const stateZipMatch = lastPart.match(STATE_ZIPCODE_REGEX);
  if (stateZipMatch) {
    return {
      state: stateZipMatch[1].trim(),
      zipcode: stateZipMatch[2].trim(),
      addressParts: parts.slice(0, -1),
    };
  }

  // Fallback: try to split by space for zipcode
  const stateZipParts = lastPart.split(' ');
  if (stateZipParts.length >= 2) {
    const lastElement = stateZipParts[stateZipParts.length - 1];
    if (/^\d{5}$/.test(lastElement)) {
      return {
        state: stateZipParts.slice(0, -1).join(' '),
        zipcode: lastElement,
        addressParts: parts.slice(0, -1),
      };
    }
  }

  // No zipcode found, treat last part as state
  return {
    state: lastPart,
    zipcode: '',
    addressParts: parts.slice(0, -1),
  };
};

/**
 * Parses a comma-separated address string for manual address input.
 *
 * Expected format: "Street, [Secondary], City, State Zipcode" or "Street, [Secondary], City, State, Zipcode"
 *
 * Examples:
 * - "123 Main St, Los Angeles, CA 90210"
 * - "123 Main St, Apt 5, Los Angeles, CA 90210"
 * - "5431 54th Street, 2nd Sub, Building 4, flr.4, Greeley, CO 80634"
 *
 * Known limitation: Street names containing commas may be parsed as separate components.
 * For complex addresses, consider using structured input fields.
 *
 * @param location - Comma-separated address string
 * @returns Parsed address object with street_line, secondary, city, state, zipcode
 */
export const parseInitialAddressForManualAddressInput = (location?: string) => {
  const empty = {
    street_line: '',
    secondary: '',
    city: '',
    state: '',
    zipcode: '',
  };

  if (!location) return empty;

  // Split by commas and trim each part
  const parts = location
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

  if (parts.length === 0) return empty;

  // Parse state and zipcode from the end
  const { state, zipcode, addressParts } = parseStateAndZip(parts);

  return createAddressResult(addressParts, state, zipcode);
};

export const currencyFormat = (
  value: string | number,
  currency: string = defaultCurrency,
  withoutDecimals: boolean = false,
) => {
  let c = currency;

  if (isEmpty(c)) {
    c = defaultCurrency;
  }

  if (c && value && value !== '-') {
    try {
      if (isArray(value)) {
        throw new TypeError(`Value can't be an array`);
      }

      const merged = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: c,
        ...(withoutDecimals && { maximumFractionDigits: 0, minimumFractionDigits: 0 }),
      }).format(parseFloat(value as string));

      const formatted = {
        sign: '',
        value: '',
        merged,
      };

      const parts = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: c,
        ...(withoutDecimals && { maximumFractionDigits: 0, minimumFractionDigits: 0 }),
      }).formatToParts(parseFloat(value as string));

      formatted.sign = parts?.find((part) => part.type === 'currency')?.value || '';
      const tmpParts = [...parts].filter((part) => part.type !== 'currency');
      formatted.value = tmpParts?.map((part) => part.value).join('');

      return formatted;
    } catch (_error) {
      return {
        sign: '',
        value: '',
        merged: '',
      };
    }
  } else {
    return {
      sign: '',
      value: '',
      merged: '',
    };
  }
};

export const pushToGoogleDataLayer = (data: GtmEvent) => {
  if (window.dataLayer) {
    window.dataLayer.push(data);
  }
};

export const identifyFullStory = (email: string, name?: string) => {
  if (isFullStoryInitialized()) {
    try {
      FullStory('setProperties', {
        type: 'user',
        properties: {
          displayName: name || email,
          email,
        },
      });
    } catch (error) {
      console.log('FullStory error: ', error);
    }
  }
};

export const trackEventFullStory = (name: string, properties: any = {}) => {
  if (isFullStoryInitialized()) {
    try {
      FullStory('trackEvent', {
        name,
        properties,
      });
    } catch (error) {
      console.log('FullStory error: ', error);
    }
  }
};

/**
 * Mapping from stepKey to FULLSTORY_EVENTS
 */
const STEP_KEY_TO_FULLSTORY_EVENT: Record<string, keyof typeof FULLSTORY_EVENTS> = {
  business_contact: 'BEGIN_ABOUT_YOU_SECTION',
  legal: 'BEGIN_ABOUT_YOUR_BUSINESS_SECTION',
  'management_&_financials': 'BEGIN_MANAGEMENT_FINANCIALS_SECTION',
  location: 'BEGIN_LOCATION_SECTION',
  prohibited_exposures: 'BEGIN_PROHIBITED_EXPOSURES_SECTION',
  classification: 'BEGIN_CLASSIFICATION_SECTION',
  property: 'BEGIN_PROPERTY_SECTION',
  liability: 'BEGIN_LIABILITY_SECTION',
  professional_services: 'BEGIN_PROFESSIONAL_SERVICES_SECTION',
  cyber: 'BEGIN_CYBER_SECTION',
  'employee_&_subcontractor_activities': 'BEGIN_EMPLOYEE_SUBCONTRACTOR_ACTIVITIES_SECTION',
  auto: 'BEGIN_AUTO_SECTION',
  owners: 'BEGIN_OWNERS_SECTION',
  'before-property': 'BEGIN_BEFORE_PROPERTY_SECTION',
  building: 'BEGIN_PROPERTIES_SECTION',
  'uw-property': 'BEGIN_UW_PROPERTY_SECTION',
  workers_compensation: 'BEGIN_WORKERS_COMPENSATION_SECTION',
  individual_owners: 'BEGIN_INDIVIDUAL_OWNERS_SECTION',
  vehicles: 'BEGIN_VEHICLES_SECTION',
  drivers: 'BEGIN_DRIVERS_SECTION',
  'driving-incidents': 'BEGIN_DRIVING_INCIDENTS_SECTION',
  claims: 'BEGIN_CLAIMS_SECTION',
};

/**
 * Handles FullStory event tracking for step completion
 * @param stepKey - The key of the step (e.g., 'business_contact')
 */
export const handleFullStoryEvent = (stepKey: string) => {
  const eventKey = STEP_KEY_TO_FULLSTORY_EVENT[stepKey];

  if (eventKey && FULLSTORY_EVENTS[eventKey]) {
    trackEventFullStory(FULLSTORY_EVENTS[eventKey]);
  }
};

export const getManualPremiumPerilsFromSubmission = (
  submission: TransformedSubmission | undefined,
) => {
  if (!submission?.data?.exposures?.employer) return [];

  const filteredPerils = submission?.data?.exposures?.employer.flatMap(
    (employer) =>
      employer.data.perils?.filter(
        (peril) => peril.name === employerExposurePerilNames.MANUAL_PREMIUM,
      ) || [],
  );

  return filteredPerils.sort((a, b) => {
    if (a.data.man_ismain === 'Yes') return -1;
    if (b.data.man_ismain === 'Yes') return 1;
    return 0;
  });
};

/**
 * Updates the `bdg_prometrix_hit` field in the state based on specific conditions.
 *
 * This function sets `bdg_prometrix_hit` only for schedule building occupancies.
 * If `isIntegrationResultsChanged` is true, it sets the `bdg_prometrix_hit` field
 * to 'No' and then updates it to 'Yes' if the `isIntegrationResultsChanged` flag is false
 * and either `bdg_erc_building` matches the `initial_integration_result.bdg_erc_building` or
 * `bdg_uw_report_type` is included in the `approvePropertyUWReportType` array.
 *
 * @param {IProperty} state - The current state object.
 * @param {PrometrixBuildingInfo} initialIntegrationResult - The initial integration result returned by Prometrix request.
 * @param {boolean} isIntegrationResultsChanged - Flag indicating if the user changed inittial integration results.
 */
export const setBdgPrometrixHitValue = (
  state: IProperty | undefined,
  initialIntegrationResult: PrometrixBuildingInfo | undefined,
  isIntegrationResultsChanged: boolean,
) => {
  if (
    !state?.bdg_occupancy ||
    !SCHEDULED_BUILDING_OCCUPANCY_VALUES.includes(state?.bdg_occupancy)
  ) {
    return undefined;
  }

  let bdg_prometrix_hit = 'No';

  if (!isIntegrationResultsChanged) {
    if (
      (state?.bdg_erc_building &&
        String(state?.bdg_erc_building) === String(initialIntegrationResult?.bdg_erc_building)) ||
      approvePropertyUWReportType.includes(state?.bdg_uw_report_type)
    ) {
      bdg_prometrix_hit = 'Yes';
    }
  }

  return bdg_prometrix_hit;
};

// TODO: There will be a feature called Product Consolidation, which will be implemented in the future.
// After this feature this should be removed and `pol_wc_included` should be used instead.
// Backend will set `pol_wc_included` field as 'Yes' or 'No'
export const isWCIncluded = (
  submission: TransformedSubmission,
): {
  isWCIncluded: boolean;
  hasPayrollRequirement: boolean;
  someOwnersOfficersIncluded: boolean;
} => {
  const someOwnersOfficersIncluded = submission.data.groups.pol_oo.some(
    (ownerOfficer) => ownerOfficer.data.pol_oo_wc_excluded === BINARY_CONSTANTS.NO,
  );

  const { payrollSubcontracted, collectsWCCerts, numEmployees, hasSubcontractors, payrollW2 } =
    submission.data.uw_questions;

  const hasPayrollRequirement = !(
    (numEmployees === '0' || payrollW2 === '0') &&
    (collectsWCCerts === 'Yes' ||
      collectsWCCerts == null ||
      payrollSubcontracted === '0' ||
      payrollSubcontracted == null ||
      hasSubcontractors === 'No' ||
      hasSubcontractors == null)
  );

  return {
    isWCIncluded: someOwnersOfficersIncluded || hasPayrollRequirement,
    hasPayrollRequirement,
    someOwnersOfficersIncluded,
  };
};

/**
 * Formats a given date (string or Date) into a specified string format.
 *
 * @param date - The date to format.
 * @param formatStr - Luxon-compatible format string.
 * @returns The formatted date string or empty string if invalid.
 */
export function formatDate(
  date: string | Date | null | undefined,
  formatStr: string = defaultDateformat,
): string {
  if (!date) return '-';

  let dt: DateTime;

  if (typeof date === 'string') {
    dt = DateTime.fromISO(date);
  } else if (date instanceof Date) {
    dt = DateTime.fromJSDate(date);
  } else {
    return '-';
  }

  return dt.isValid ? dt.toFormat(formatStr) : '-';
}

/**
 * Check if the condition is for scheduled buildings
 */
export const isScheduledBuildingCondition = (condition: ShowCondition | undefined) =>
  condition?.reference === BDG_OCCUPANCY_CODE &&
  isArray(condition?.value) &&
  every(condition?.value, (v) => includes(SCHEDULED_BUILDING_OCCUPANCY_VALUES, v));

/**
 * Recursively check if any condition (or nested condition) is for scheduled buildings
 */
export const hasScheduledBuildingCondition = (condition: ShowCondition | undefined): boolean => {
  if (isScheduledBuildingCondition(condition)) {
    return true;
  }

  if (isArray(condition?.operations)) {
    return condition.operations.some((op) => hasScheduledBuildingCondition(op));
  }

  return false;
};

const rewriteCondition = (condition: ShowCondition): ShowCondition => {
  if (isScheduledBuildingCondition(condition)) {
    // Built here, not hoisted: `common/constants` imports this module, so a module-level read is `undefined`.
    return {
      reference: HAS_SCHEDULED_BUILDINGS_CODE,
      relational_operator: '==',
      value: true,
      value_type: 'boolean',
    };
  }

  return isArray(condition.operations)
    ? { ...condition, operations: condition.operations.map(rewriteCondition) }
    : condition;
};

/**
 * Swap each `bdg_occupancy` scheduled-building test for the derived `hasScheduledBuildings` flag,
 * keeping the rest of the tree so the gates ANDed onto it still apply (ENG-12240).
 */
export const rewriteScheduledBuildingConditions = (
  condition: ShowCondition | undefined,
): ShowCondition | undefined => (condition ? rewriteCondition(condition) : condition);

/** Whether a condition, or any condition nested under it, reads `reference`. */
export const conditionReadsReference = (
  condition: ShowCondition | undefined,
  reference: string,
): boolean => {
  if (!condition) return false;
  if (condition.reference === reference) return true;

  return isArray(condition.operations)
    ? condition.operations.some((op) => conditionReadsReference(op, reference))
    : false;
};

export const determineOwnerOfficerPositions = (entity: string) => {
  const commonDescription =
    'Only include individuals that perform work for your business or are involved in managing or directing day-to-day operations. Do not include outside or silent investors.';

  const corporateDescription =
    'This includes President, Vice President, and/or other officer titles that were officially appointed or approved by ownership or the Board of Directors to manage or perform daily operations.';

  const partnerDescription =
    'Only include individual partners. Do not include non-person entities.';

  const titlesMap: Record<string, { title: string; description: string }> = {
    [entityTypes.SOLE_PROPRIETOR.code]: {
      title: 'List your business owner',
      description: commonDescription,
    },
    [entityTypes.LLC.code]: {
      title: 'List all LLC members',
      description: commonDescription,
    },
    [entityTypes.CORPORATION.code]: {
      title: 'List all corporate officers',
      description: corporateDescription,
    },
    [entityTypes.CORPORATION_S_CORP.code]: {
      title: 'List all corporate officers',
      description: corporateDescription,
    },
    [entityTypes.CORPORATION_OTHER.code]: {
      title: 'List all corporate officers',
      description: corporateDescription,
    },
    [entityTypes.NONPROFIT_ORG.code]: {
      title: 'List all corporate officers',
      description: corporateDescription,
    },
    [entityTypes.PARTNERSHIP.code]: {
      title: 'List all partners',
      description: partnerDescription,
    },
    [entityTypes.TRUST.code]: {
      title: 'List all trustees',
      description: commonDescription,
    },
  };

  return (
    titlesMap[entity] ?? { title: 'List your business owners', description: commonDescription }
  );
};

/**
 * Check if a field value contains multiple business names based on special characters or words
 * @param fieldValue - The field value to check
 * @returns True if the field contains multiple names indicators, false otherwise
 */
export const containsMultipleNames = (fieldValue: string | undefined): boolean => {
  const specialCharacters = ['&', '|', '/', ':', ';', ',', '\\'];
  const specialWords = ['or', 'and'];

  if (fieldValue) {
    const lowerFieldValueArray = fieldValue.toLowerCase().split(' ');

    // Check for special characters
    if (specialCharacters.some((char) => fieldValue.includes(char))) {
      return true;
    }
    // Check for special words (case-insensitive)
    if (specialWords.some((word) => lowerFieldValueArray.includes(word))) {
      return true;
    }
  }

  return false;
};

export const checkWorkersCompensationAvailabilityByState = (state: string) => {
  return ![STATES.OH, STATES.NY].includes(state);
};

export const getWCExclusionInfo = ({
  policyTaxKeyword,
  policyState,
}: WCExclusionParams): WCExclusionResult | null => {
  const isWCAvailabilityByState = checkWorkersCompensationAvailabilityByState(policyState);
  const isExcludedIndustry = wcExcludedIndustryNames.includes(policyTaxKeyword);

  // State eligibility (OH / NY)
  if (!isWCAvailabilityByState) {
    return {
      isWCExcludedReason: 'wcNotAvailableInState',
      warningText:
        WC_STATE_EXCLUSION_MESSAGES[policyState] ??
        `This quote will not include Workers' Compensation coverage in ${STATES_FULLNAME[policyState]}.`,
    };
  }
  //  Primary industry exclusion
  if (isExcludedIndustry) {
    return {
      isWCExcludedReason: 'wcNotAvailableInPrimaryIndustry',
      warningText:
        'This class of business is not eligible for Workers’ Compensation coverage with THREE. Please proceed to quote other coverage.',
    };
  }

  return null;
};

//https://linear.app/three-insurance/issue/ENG-9473/consumer-portal-update-vehicledriver-validation-for-v171-increase
/**
 * Parse version number from three_config_version string for semantic version comparison.
 *
 * Converts semantic version strings like "V14.12" into a comparable number format
 * by treating major and minor versions separately: major * 100 + minor.
 * This ensures correct comparison: V14.12 (1412) > V14.2 (1402).
 *
 * @param versionString - Version string in format "V14.12" or "v17.1"
 * @returns Comparable version number (e.g., V14.12 → 1412, V17.1 → 1701) or 0 if parsing fails
 *
 * @example
 * ```typescript
 * parseConfigVersion("V14.12") // returns 1412
 * parseConfigVersion("v17.1")  // returns 1701
 * parseConfigVersion("V17.10") // returns 1710
 * parseConfigVersion("invalid") // returns 0
 *
 * // Comparison works correctly:
 * parseConfigVersion("V14.12") > parseConfigVersion("V14.2")   // true (1412 > 1402)
 * parseConfigVersion("V17.10") > parseConfigVersion("V17.1")   // true (1710 > 1701)
 * ```
 */
export const parseConfigVersion = (versionString?: string): number => {
  if (!versionString) return 0;

  // Remove 'V' or 'v' prefix
  const cleanVersion = versionString.replace(/^[Vv]/, '');

  // Split into major and minor parts
  const parts = cleanVersion.split('.');
  const major = parseInt(parts[0], 10);
  const minor = parts[1] ? parseInt(parts[1], 10) : 0;

  // Validate parsed values
  if (isNaN(major) || isNaN(minor)) return 0;
  if (minor >= 100) {
    console.warn(`Version minor part ${minor} exceeds 99, which may cause comparison issues`);
  }

  // Convert to comparable number: major * 100 + minor
  // V14.12 → 1412, V17.1 → 1701, V17.10 → 1710
  return major * 100 + minor;
};

/**
 * Get maximum vehicle count for driver validation based on policy version.
 *
 * @param policyVersion - The policy version number from three_config_version
 * @returns Maximum vehicle count where driver validation applies (10 for v17.1+, 5 for earlier)
 */
export const getDriverValidationMaxVehicleCount = (policyVersion: number): number => {
  return policyVersion >= DRIVER_VEHICLE_VALIDATION_LOWER_LIMIT_VERSION ? 10 : 5;
};

/** GK limit version gate: v16.9+ (below v17), or v17.3+. */
export const isGaragekeepersLimitExtractedFromLocationForVersion = (
  threeConfigVersion?: string,
): boolean => {
  const version = parseConfigVersion(threeConfigVersion);
  if (!version) return false;
  return (version >= 1609 && version < 1700) || version >= 1703;
};

/**
 * Determines whether the drivers step should be skipped based on vehicle count and policy version.
 *
 * This function implements version-specific validation rules for the driver/vehicle relationship:
 *
 * **For v17.1+ policies:**
 * - 0 vehicles → skip drivers step (no vehicles, no drivers needed)
 * - 1-10 vehicles → show drivers step (at least 1 driver required)
 * - 11+ vehicles → skip drivers step (validation doesn't apply)
 *
 * **For pre-v17.1 policies:**
 * - 0 vehicles → skip drivers step (no vehicles, no drivers needed)
 * - 1-5 vehicles → show drivers step (at least 1 driver required)
 * - 6+ vehicles → skip drivers step (old validation doesn't apply)
 */
export const shouldSkipDriversStep = (submission?: TransformedSubmission) => {
  const vehicleCount = submission?.data?.exposures?.vehicle?.length || 0;
  const configVersion = parseConfigVersion(submission?.three_config_version);
  const maxVehicleCount = getDriverValidationMaxVehicleCount(configVersion);

  // More than maxVehicleCount vehicles → skip drivers step (validation doesn't apply)
  if (vehicleCount > maxVehicleCount) {
    return true;
  }

  // 1 to maxVehicleCount vehicles → don't skip drivers step (need to validate drivers)
  return false;
};

/**
 * Converts a raw three_config_version (e.g. "V14_12", "V17") into
 * dot-notation suitable for show condition comparisons (e.g. "14.12", "17").
 */
export const formatThreeConfigVersion = (threeConfigVersion?: string): string | undefined =>
  threeConfigVersion?.replace(/^V/, '').replace(/_/g, '.');

/**
 * Splits a version string (e.g. "14.11", "V17.1") into [major, minor] integers.
 * Cannot use parseFloat because 14.11 < 14.9 as floats, but V14.11 > V14.9 in version semantics.
 */
export const parseProductVersion = (value: string | number): [number, number] => {
  const str = String(value).replace(/^V/, '');
  const parts = str.split('.');
  return [parseInt(parts[0], 10) || 0, parseInt(parts[1], 10) || 0];
};

/**
 * Compares two product versions using major.minor integer comparison.
 * Returns the result of applying the given relational operator.
 */
export const compareProductVersions = (
  referenceValue: string | number,
  conditionValue: string | number,
  operator: string,
): boolean => {
  const [referenceMajor, referenceMinor] = parseProductVersion(referenceValue);
  const [conditionMajor, conditionMinor] = parseProductVersion(conditionValue);
  const comparison =
    referenceMajor !== conditionMajor
      ? referenceMajor - conditionMajor
      : referenceMinor - conditionMinor;

  const operators: Record<string, () => boolean> = {
    '>': () => comparison > 0,
    '<': () => comparison < 0,
    '<=': () => comparison <= 0,
    '>=': () => comparison >= 0,
    '==': () => comparison === 0,
    '!=': () => comparison !== 0,
  };
  return operators[operator]?.() ?? false;
};

export const matchesExpandedHotelPayrollIndustry = (value: string | null | undefined): boolean => {
  const trimmed = value?.trim();
  return Boolean(trimmed && EXPANDED_HOTEL_PAYROLL_WORKSHEET_INDUSTRIES.includes(trimmed));
};

export const isExpandedHotelPayrollWorksheet = (
  polTaxKeyword: string | null | undefined,
  fields: Array<{ code?: string }> | null | undefined,
): boolean =>
  matchesExpandedHotelPayrollIndustry(polTaxKeyword) &&
  fieldsContainAnyCode(fields, Object.values(EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES));
